import { useEffect, useMemo, useState } from "react";

import Modal, { ModalProps } from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { toast } from "react-toastify";
import Badge from "react-bootstrap/Badge";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { useDebounce } from "@/hooks/useDebounce";
import { HealthUnit } from "@/@types/esus/HealthUnit";
import {
	CityEsusRoleEnum,
	CitySIntegradaRoleEnum,
	StateEsusRoleEnum,
} from "@/utils/enums/RoleEnum";
import { Paginated } from "@/@types/generics/paginated";
import { Option } from "@/@types/generics/Option";
import { updateAccessUserAccount, UsersAccountAccess } from "@/services/app/userAccountService";
import { getUnitsPaginated } from "@/services/app/healthUnitService";
import statesAndCities from "@/utils/static/states-and-cities.json";
import { ModuleEnum } from "@/utils/enums/ModuleEnum";
import { StatesAndCities } from "@/@types/statesAndCities";

import { SearchInput } from "@/components/SearchInput";
import { ProfessionalUnitAccessTable } from "./ProfessionalUnitAccessTable";
import { ProfessionalUnitAccessFilters } from "./ProfessionalUnitAccessFilters";

type Props = ModalProps & {
	selectedUser: UsersAccountAccess;
	setRefreshPage: () => void;
};

export function ModalProfessionalUnitAccess({ selectedUser, setRefreshPage, ...rest }: Props) {
	const statesAndCitiesJson = statesAndCities as StatesAndCities;
	const { uf, ibgeCode, user, module, currentAccount } = useCurrentAccount();
	const [healthUnitsPaginated, setHealthUnitsPaginated] = useState<Paginated<HealthUnit>>(
		{} as Paginated<HealthUnit>
	);
	const [selectedCnesListModal, setSelectedCnesListModal] = useState<string[]>([]);
	const [selectedOptionRoleModal, setSelectedOptionRoleModal] = useState<Option<string>>({
		label: "",
		value: "",
	});
	const [searchUnit, setSearchUnit] = useState("");

	const [isLoading, setIsLoading] = useState(false);
	const debouncedSearchunit = useDebounce(searchUnit, 1000);

	const [pageNumber, setPageNumber] = useState(1);

	const defaultOptionModule: Option<ModuleEnum | undefined> = user.isAdmin
		? {
				label: "Todos os modulos",
				value: undefined,
		  }
		: {
				label:
					ModuleEnum.ESUS_APS === module
						? "Esus APS"
						: ModuleEnum.SINTEGRADA === module
						? "Sintegrada"
						: "",
				value: module,
		  };

	const defaultCityOption: Option<string> = user.isAdmin
		? {
				label: "Todas as cidades",
				value: "",
		  }
		: {
				label:
					statesAndCitiesJson.cities.find((s) => String(s.id) === ibgeCode)?.nome || "",
				value: ibgeCode,
		  };

	const defaultFederalUnitOption: Option<string> = user.isAdmin
		? { label: "Todos os estados", value: "" }
		: { label: uf, value: uf };

	const [selectedOptionModuleModal, setSelectedOptionModuleModal] =
		useState<Option<ModuleEnum | undefined>>(defaultOptionModule);
	const [selectedOptionFederalUnitModal, setSelectedOptionFederalUnitModal] =
		useState<Option<string>>(defaultFederalUnitOption);
	const [selectedOptionCityModal, setSelectedOptionCityModal] =
		useState<Option<string>>(defaultCityOption);

	async function handleFetchUnitsPaginated() {
		if (!selectedOptionCityModal.value) {
			return;
		}
		setIsLoading(true);
		setHealthUnitsPaginated(
			await getUnitsPaginated({
				uf: selectedOptionFederalUnitModal.value,
				ibgeCode: selectedOptionCityModal.value,
				pageNumber: pageNumber,
				name: searchUnit,
				module: selectedOptionModuleModal?.value || module,
			})
		);
		setIsLoading(false);
	}

	async function handleUpdateProfessionalAccess() {
		const slug = `${selectedUser?.fullName}:`;

		const isManagement =
			String(selectedOptionRoleModal?.value) === StateEsusRoleEnum.STATE_ADMIN ||
			String(selectedOptionRoleModal?.value) === CityEsusRoleEnum.ESUS_ADMIN ||
			String(selectedOptionRoleModal?.value) === CityEsusRoleEnum.ESUS_MANAGER ||
			String(selectedOptionRoleModal?.value) === CitySIntegradaRoleEnum.SINTEGRADA_ADMIN;

		const cnesList = !isManagement ? selectedCnesListModal : [];

		const isUpdated = await updateAccessUserAccount({
			userId: selectedUser?.userId,
			uf: selectedOptionFederalUnitModal?.value || uf,
			ibgeCode: selectedOptionCityModal?.value || ibgeCode,
			cnesList: cnesList,
			roleId: selectedOptionRoleModal?.value,
			module: selectedOptionModuleModal?.value || module,
		});

		if (isUpdated) {
			setRefreshPage();
			toast.info(`${slug} Atualizado com Sucesso`, { autoClose: 1500 });
			rest.onHide && rest.onHide();
		} else {
			toast.error(`${slug} deve solicitar o acesso previamente`, {
				autoClose: 1500,
			});
		}
	}

	function shouldShowUnitAccessTable() {
		const role = String(selectedOptionRoleModal?.value);
		return (
			role !== StateEsusRoleEnum.STATE_ADMIN &&
			role !== CityEsusRoleEnum.ESUS_ADMIN &&
			role !== CityEsusRoleEnum.ESUS_MANAGER &&
			role !== CitySIntegradaRoleEnum.SINTEGRADA_ADMIN &&
			selectedOptionRoleModal?.value &&
			selectedOptionCityModal.value
		);
	}

	useMemo(() => {
		const selectionOfUnitCnesWithUserAccess =
			selectedUser.accessByCNES
				?.filter(
					(h) =>
						(h.key.cityId === currentAccount.id ||
							h.key.ibgeCode === selectedOptionCityModal.value) &&
						selectedOptionModuleModal.value?.toString() === h.key.moduleId?.toString()
				)
				.map((healhUnit) => healhUnit.key.cnes) || [];
		setSelectedCnesListModal(selectionOfUnitCnesWithUserAccess);
	}, [selectedUser, currentAccount, selectedOptionModuleModal, selectedOptionCityModal]);

	useEffect(() => {
		handleFetchUnitsPaginated();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedUser, selectedOptionCityModal.value, pageNumber, debouncedSearchunit]);

	useEffect(() => {
		if (pageNumber !== 1) {
			setPageNumber(1);

			return;
		}
		handleFetchUnitsPaginated();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearchunit]);

	return (
		<>
			<Modal {...rest} size="xl">
				<Modal.Header closeButton>
					<Modal.Title>
						Acesso do Profissional
						<label className="d-flex text-primary fs-6 ">
							{selectedUser?.fullName}
						</label>
						{selectedUser.role && (
							<Badge bg="muted">{selectedUser.role.description}</Badge>
						)}
					</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					<ProfessionalUnitAccessFilters
						selectedUser={selectedUser}
						selectedOptionModuleModal={selectedOptionModuleModal}
						setSelectedOptionModuleModal={setSelectedOptionModuleModal}
						selectedOptionFederalUnitModal={selectedOptionFederalUnitModal}
						setSelectedOptionFederalUnitModal={setSelectedOptionFederalUnitModal}
						selectedOptionCityModal={selectedOptionCityModal}
						setSelectedOptionCityModal={setSelectedOptionCityModal}
						selectedOptionRoleModal={selectedOptionRoleModal}
						setSelectedOptionRoleModal={setSelectedOptionRoleModal}
						selectedCnesList={selectedCnesListModal}
						setSelectedCnesList={setSelectedCnesListModal}
						handleUpdateProfessionalAccess={handleUpdateProfessionalAccess}
						fetchUnitsPaginated={handleFetchUnitsPaginated}
					/>

					{shouldShowUnitAccessTable() && (
						<>
							<Row className="d-flex align-items-start mb-5">
								<Col
									sm={12}
									md={6}
									lg={6}
									className="d-flex flex-column justify-content-start"
								>
									<label className="form-label">Pesquisar Unidade</label>
									<SearchInput
										placeholder="Pesquisar Unidade"
										value={searchUnit}
										setValue={setSearchUnit}
									/>
								</Col>
							</Row>
							<ProfessionalUnitAccessTable
								pageNumber={pageNumber}
								setPageNumber={setPageNumber}
								selectionOfUnitCnesWithUserAccess={selectedCnesListModal}
								setSelectedCnesList={setSelectedCnesListModal}
								healthUnitsPaginated={healthUnitsPaginated}
								isLoading={isLoading}
							/>
						</>
					)}
				</Modal.Body>
			</Modal>
		</>
	);
}
